import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import PrototypeLayout from '../PrototypeLayout';
import { colors, font, type as ty, layout } from '../theme/tokens';
import { heat } from '../theme/tokens';
import { getStops, StopData, ROUTE_CAPTION, TRIPS } from '../data/stops';
import { useFleet } from '../FleetContext';
import { doorLabel } from '../data/doors';
import { useI18n } from '../i18n';
import {
  panel, Badge, PageHeader, PanelTitle, DemoFooter, Tooltip, emptyTip, TipState,
} from '../components/ui';

// ── Helpers ──────────────────────────────────────────────────────────────────

// Series colours: chosen for legibility on white and to stay distinct from the
// brand yellow, which is too light to read as a 2px line.
const WAGEN_COLORS: Record<string, string> = {
  A: '#4456F2',
  B: '#2E7D53',
  C: '#7A4FA3',
  D: '#B58900',
};

function healthColor(h: number) {
  if (h >= 80) return colors.ok;
  if (h >= 60) return colors.warn;
  return colors.alert;
}

function healthLabel(h: number, t: (k: string) => string) {
  if (h >= 80) return t('common.good');
  if (h >= 60) return t('common.monitor');
  return t('common.action');
}

function fmtMs(ms: number) {
  if (ms === 0) return '—';
  const s = Math.round(ms / 1000);
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
}

/** Map an openings count onto the shared severity ramp. */
function openingsHeat(openings: number, max: number) {
  if (openings === 0) {
    return { fill: colors.bgAlt, stroke: colors.border, text: colors.inkGhost };
  }
  const frac = max > 0 ? openings / max : 0;
  const idx = frac < 0.25 ? 1 : frac < 0.5 ? 2 : frac < 0.75 ? 3 : frac < 0.9 ? 4 : 5;
  return heat[idx]!;
}

// ── Trip overview chart ──────────────────────────────────────────────────────

const OverviewChart: React.FC<{
  stops: StopData[];
  selectedStop: number;
  onSelectStop: (i: number) => void;
}> = ({ stops, selectedStop, onSelectStop }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tip, setTip] = useState<TipState>(emptyTip);
  const { t } = useI18n();

  // Memoised so the draw effect doesn't re-run on every render.
  const wagenLines = useMemo(() => ({
    A: stops.map(s => s.doors.filter(d => d.doorId.startsWith('A')).reduce((a, b) => a + b.openings, 0)),
    B: stops.map(s => s.doors.filter(d => d.doorId.startsWith('B')).reduce((a, b) => a + b.openings, 0)),
    C: stops.map(s => s.doors.filter(d => d.doorId.startsWith('C')).reduce((a, b) => a + b.openings, 0)),
    D: stops.map(s => s.doors.filter(d => d.doorId.startsWith('D')).reduce((a, b) => a + b.openings, 0)),
  }), [stops]);

  const totalLine = useMemo(
    () => stops.map(s => s.doors.reduce((a, b) => a + b.openings, 0)),
    [stops],
  );
  const maxVal = useMemo(() => Math.max(...totalLine, 1), [totalLine]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const ns = 'http://www.w3.org/2000/svg';
    const W = 800, H = 168;
    const padL = 38, padR = 16, padT = 16, padB = 34;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;
    const xStep = chartW / Math.max(1, stops.length - 1);

    const mkEl = (tag: string, attrs: Record<string, string>) => {
      const e = document.createElementNS(ns, tag);
      Object.entries(attrs).forEach(([k, v]) => e.setAttribute(k, v));
      return e;
    };
    const xPos = (i: number) => padL + i * xStep;
    const yPos = (v: number) => padT + chartH - (v / maxVal) * chartH;

    const cleanup: Array<() => void> = [];

    // Grid
    [0, 0.25, 0.5, 0.75, 1].forEach(frac => {
      const y = padT + chartH - frac * chartH;
      svg.appendChild(mkEl('line', {
        x1: String(padL), y1: String(y), x2: String(W - padR), y2: String(y),
        stroke: frac === 0 ? colors.borderStrong : colors.border, 'stroke-width': '1',
      }));
      if (frac > 0) {
        const t = mkEl('text', { x: String(padL - 8), y: String(y + 4), 'text-anchor': 'end', 'font-size': '9', fill: colors.inkFaint, 'font-family': font.mono });
        t.textContent = String(Math.round(frac * maxVal));
        svg.appendChild(t);
      }
    });

    // Selected stop band
    const sx = xPos(selectedStop);
    svg.appendChild(mkEl('rect', {
      x: String(sx - xStep * 0.4), y: String(padT),
      width: String(xStep * 0.8), height: String(chartH),
      fill: colors.accentWash, stroke: colors.accent, 'stroke-width': '1', rx: '2',
    }));

    // Lines then dots
    Object.entries(wagenLines).forEach(([wagen, vals]) => {
      svg.appendChild(mkEl('polyline', {
        points: vals.map((v, i) => `${xPos(i)},${yPos(v)}`).join(' '),
        fill: 'none', stroke: WAGEN_COLORS[wagen], 'stroke-width': '2',
        'stroke-linejoin': 'round', 'stroke-linecap': 'round',
      }));
    });
    Object.entries(wagenLines).forEach(([wagen, vals]) => {
      vals.forEach((v, i) => {
        svg.appendChild(mkEl('circle', {
          cx: String(xPos(i)), cy: String(yPos(v)), r: '3',
          fill: WAGEN_COLORS[wagen], stroke: colors.bg, 'stroke-width': '1.5',
        }));
      });
    });

    // Labels + hit areas
    stops.forEach((s, i) => {
      const x = xPos(i);
      const hit = mkEl('rect', {
        x: String(x - xStep * 0.45), y: String(padT),
        width: String(xStep * 0.9), height: String(chartH),
        fill: 'transparent', style: 'cursor:pointer',
      });
      const onClick = () => onSelectStop(i);
      const content =
        `${s.code} · ${s.stopName} · ${s.arrivalTime}\n` +
        `${t('p2.totalOpenings')}: ${totalLine[i]}\n` +
        `A ${wagenLines.A[i]}  B ${wagenLines.B[i]}  C ${wagenLines.C[i]}  D ${wagenLines.D[i]}`;
      const onEnter = (e: MouseEvent) => setTip({ visible: true, x: e.clientX + 14, y: e.clientY - 60, content });
      const onMove = (e: MouseEvent) => setTip(t => ({ ...t, x: e.clientX + 14, y: e.clientY - 60 }));
      const onLeave = () => setTip(t => ({ ...t, visible: false }));

      hit.addEventListener('click', onClick);
      hit.addEventListener('mouseenter', onEnter);
      hit.addEventListener('mousemove', onMove);
      hit.addEventListener('mouseleave', onLeave);
      cleanup.push(() => {
        hit.removeEventListener('click', onClick);
        hit.removeEventListener('mouseenter', onEnter);
        hit.removeEventListener('mousemove', onMove);
        hit.removeEventListener('mouseleave', onLeave);
      });
      svg.appendChild(hit);

      const lbl = mkEl('text', {
        x: String(x), y: String(H - 8), 'text-anchor': 'middle', 'font-size': '9',
        fill: i === selectedStop ? colors.ink : colors.inkFaint,
        'font-family': font.mono,
        'font-weight': i === selectedStop ? '700' : '400',
      });
      // Three-letter codes never truncate on the axis; the full callsign is in
      // the tooltip and the stop banner.
      lbl.textContent = s.code;
      svg.appendChild(lbl);
    });

    return () => { cleanup.forEach(fn => fn()); };
  }, [stops, selectedStop, onSelectStop, wagenLines, maxVal, totalLine, t]);

  return (
    <Box sx={{ ...panel, mb: 3 }}>
      <PanelTitle
        right={
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {(['A', 'B', 'C', 'D'] as const).map(w => (
              <Box key={w} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Box sx={{ width: 18, height: 3, backgroundColor: WAGEN_COLORS[w], borderRadius: '2px' }} />
                <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkMuted }}>
                  {t('p3.wagen')} {w}
                </Typography>
              </Box>
            ))}
          </Box>
        }
      >
        {t('p2.overview')}
      </PanelTitle>
      <svg ref={svgRef} viewBox="0 0 800 168" style={{ display: 'block', width: '100%' }} role="img">
        <title>Door openings per stop, split by carriage</title>
      </svg>
      <Tooltip tip={tip} />
    </Box>
  );
};

// ── Stop selector ────────────────────────────────────────────────────────────

const StopSelector: React.FC<{
  stops: StopData[];
  selected: number;
  onChange: (i: number) => void;
}> = ({ stops, selected, onChange }) => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
    {stops.map((s, i) => {
      const active = i === selected;
      const flagged = s.doors.some(d => d.forcedEvents > 0 || d.health < 65);
      return (
        <Button
          key={s.stopId}
          onClick={() => onChange(i)}
          disableRipple
          sx={{
            fontFamily: font.mono,
            fontSize: '12px',
            fontWeight: active ? 700 : 500,
            px: 1.75,
            py: 0.75,
            minWidth: 0,
            borderRadius: layout.radius,
            textTransform: 'none',
            position: 'relative',
            backgroundColor: active ? colors.accent : colors.surface,
            border: `1px solid ${active ? colors.accent : colors.border}`,
            color: active ? colors.ink : colors.inkMuted,
            '&:hover': {
              backgroundColor: active ? colors.accent : colors.accentWash,
              borderColor: active ? colors.accentPress : colors.accent,
              color: colors.ink,
            },
          }}
        >
          {s.code}
          <Box component="span" sx={{ ml: 0.75, fontWeight: 400, opacity: active ? 0.75 : 0.6 }}>
            {s.stopName}
          </Box>
          {flagged && (
            <Box sx={{
              position: 'absolute', top: 4, right: 4,
              width: 6, height: 6, borderRadius: '50%',
              backgroundColor: colors.alert,
            }} />
          )}
        </Button>
      );
    })}
  </Box>
);

// ── Stop heatmap ─────────────────────────────────────────────────────────────

const StopHeatmap: React.FC<{ stop: StopData }> = ({ stop }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tip, setTip] = useState<TipState>(emptyTip);
  const { t } = useI18n();
  const maxO = useMemo(() => Math.max(...stop.doors.map(d => d.openings), 1), [stop]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const ns = 'http://www.w3.org/2000/svg';
    const mkEl = (tag: string, attrs: Record<string, string>) => {
      const e = document.createElementNS(ns, tag);
      Object.entries(attrs).forEach(([k, v]) => e.setAttribute(k, v));
      return e;
    };

    const W = 640, cabW = 36, gap = 6, numCars = 4;
    const bodyW = (W - cabW * 2 - gap * (numCars + 1)) / numCars;
    const bodyH = 130, trainY = 44;
    const midY = trainY + bodyH / 2;
    const carStartX = gap + cabW + gap;
    const rearCabX = carStartX + bodyW * numCars + gap * numCars;
    const doorW = 38, doorH = 18, doorInset = 11, doorMargin = 13;

    const cleanup: Array<() => void> = [];

    [trainY - 15, trainY + bodyH + 15].forEach(tYy => {
      svg.appendChild(mkEl('line', { x1: '8', y1: String(tYy), x2: '632', y2: String(tYy), stroke: colors.border, 'stroke-width': '3', 'stroke-linecap': 'round' }));
    });

    [gap, rearCabX].forEach(x => {
      svg.appendChild(mkEl('rect', { x: String(x), y: String(trainY), width: String(cabW), height: String(bodyH), rx: '4', fill: colors.bgAlt, stroke: colors.borderStrong, 'stroke-width': '1' }));
    });

    (['A', 'B', 'C', 'D'] as const).forEach((wagen, wi) => {
      const cx = carStartX + wi * (bodyW + gap);
      svg.appendChild(mkEl('rect', { x: String(cx), y: String(trainY), width: String(bodyW), height: String(bodyH), rx: '2', fill: colors.bg, stroke: colors.border, 'stroke-width': '1' }));
      svg.appendChild(mkEl('line', { x1: String(cx + 8), y1: String(midY), x2: String(cx + bodyW - 8), y2: String(midY), stroke: colors.border, 'stroke-width': '0.75', 'stroke-dasharray': '4 3' }));

      const lbl = mkEl('text', { x: String(cx + bodyW / 2), y: String(midY), 'text-anchor': 'middle', 'dominant-baseline': 'central', 'font-size': '11', fill: colors.inkGhost, 'font-family': font.mono, 'font-weight': '500', 'letter-spacing': '0.06em' });
      lbl.textContent = `Wagen ${wagen}`;
      svg.appendChild(lbl);

      const doorDefs = [
        { id: `${wagen}1L`, side: 'L', x: cx + doorMargin },
        { id: `${wagen}2L`, side: 'L', x: cx + bodyW - doorMargin - doorW },
        { id: `${wagen}1R`, side: 'R', x: cx + doorMargin },
        { id: `${wagen}2R`, side: 'R', x: cx + bodyW - doorMargin - doorW },
      ];

      doorDefs.forEach(dd => {
        const d = stop.doors.find(x => x.doorId === dd.id);
        if (!d) return;
        const dy = dd.side === 'L'
          ? trainY + doorInset
          : trainY + bodyH - doorInset - doorH;

        const c = openingsHeat(d.openings, maxO);
        const g = mkEl('g', { style: 'cursor:pointer' });
        g.appendChild(mkEl('rect', { x: String(dd.x), y: String(dy), width: String(doorW), height: String(doorH), rx: '2', fill: c.fill, stroke: c.stroke, 'stroke-width': '1.25' }));

        const txt = mkEl('text', { x: String(dd.x + doorW / 2), y: String(dy + doorH / 2), 'text-anchor': 'middle', 'dominant-baseline': 'central', 'font-size': '9', fill: c.text, 'font-family': font.mono, 'font-weight': '500' });
        txt.textContent = d.openings > 0 ? String(d.openings) : '—';
        g.appendChild(txt);

        if (d.forcedEvents > 0) {
          g.appendChild(mkEl('circle', { cx: String(dd.x + doorW - 3), cy: String(dy + 3), r: '4', fill: colors.alert, stroke: colors.bg, 'stroke-width': '1.5' }));
        }

        const content =
          `${doorLabel(dd.id)}  ·  ${dd.side === 'L' ? t('common.platform') : t('common.trackside')}\n` +
          `${t('p2.openings')}: ${d.openings}  ·  ${t('p2.totalOpenTime')}: ${fmtMs(d.totalOpenMs)}\n` +
          `${t('p2.avg')}: ${fmtMs(d.avgOpenMs)}\n` +
          `${t('p2.forcedEvents')}: ${d.forcedEvents}  ·  ${t('common.health')}: ${d.health}%`;

        const onEnter = (e: MouseEvent) => setTip({ visible: true, x: e.clientX + 14, y: e.clientY - 68, content });
        const onMove = (e: MouseEvent) => setTip(t => ({ ...t, x: e.clientX + 14, y: e.clientY - 68 }));
        const onLeave = () => setTip(t => ({ ...t, visible: false }));
        g.addEventListener('mouseenter', onEnter);
        g.addEventListener('mousemove', onMove);
        g.addEventListener('mouseleave', onLeave);
        cleanup.push(() => {
          g.removeEventListener('mouseenter', onEnter);
          g.removeEventListener('mousemove', onMove);
          g.removeEventListener('mouseleave', onLeave);
        });

        svg.appendChild(g);
      });
    });

    return () => { cleanup.forEach(fn => fn()); };
  }, [stop, maxO, t]);

  return (
    <Box>
      <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint, mb: 1 }}>
        {t('p2.sideL')}
      </Typography>
      <svg ref={svgRef} viewBox="0 0 640 220" style={{ display: 'block', width: '100%' }} role="img">
        <title>{t('p2.heatmapTitle')}</title>
      </svg>
      <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint, mt: 1 }}>
        {t('p2.sideR')}
      </Typography>
      <Tooltip tip={tip} />
    </Box>
  );
};

// ── Door table ───────────────────────────────────────────────────────────────

const GRID = '78px 1.1fr 1.5fr 96px 76px';

type SortCol = 'doorId' | 'openings' | 'totalOpenMs' | 'health';

const DoorTable: React.FC<{ stop: StopData }> = ({ stop }) => {
  const { t } = useI18n();
  // Column and direction live in one state object: updating them together keeps
  // the updater pure, which matters under StrictMode double-invocation.
  const [sortBy, setSortBy] = useState<{ col: SortCol; dir: 1 | -1 }>({
    col: 'openings',
    dir: -1,
  });

  const sorted = useMemo(() => [...stop.doors].sort((a, b) => {
    // Sort the door column by its displayed label, so A1…A4 read in order.
    const av = sortBy.col === 'doorId' ? doorLabel(a.doorId) : a[sortBy.col] as number | string;
    const bv = sortBy.col === 'doorId' ? doorLabel(b.doorId) : b[sortBy.col] as number | string;
    return av < bv ? sortBy.dir : av > bv ? -sortBy.dir : 0;
  }), [stop, sortBy]);

  const maxO = useMemo(() => Math.max(...stop.doors.map(d => d.openings), 1), [stop]);

  const toggle = useCallback((col: SortCol) => {
    setSortBy(s => (s.col === col ? { col, dir: (s.dir === 1 ? -1 : 1) as 1 | -1 } : { col, dir: -1 }));
  }, []);

  const Th: React.FC<{ col: SortCol; label: string }> = ({ col, label }) => (
    <Typography
      onClick={() => toggle(col)}
      sx={{
        ...ty.eyebrow,
        fontSize: '10px',
        color: sortBy.col === col ? colors.ink : colors.inkFaint,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { color: colors.ink },
      }}
    >
      {label}{sortBy.col === col ? (sortBy.dir === -1 ? ' ↓' : ' ↑') : ''}
    </Typography>
  );

  return (
    <Box sx={{ ...panel, p: 0, overflow: 'hidden' }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: GRID,
        gap: 1.5,
        px: 2.5,
        py: 1.5,
        backgroundColor: colors.bgAlt,
        borderBottom: `1px solid ${colors.border}`,
      }}>
        <Th col="doorId" label={t('common.door')} />
        <Th col="openings" label={t('p2.openings')} />
        <Th col="totalOpenMs" label={t('p2.totalOpenTime')} />
        <Th col="health" label={t('common.health')} />
        <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint }}>{t('p2.flags')}</Typography>
      </Box>

      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
        {sorted.map((door, i) => {
          const isL = door.doorId.endsWith('L');
          const barPct = (door.openings / maxO) * 100;
          return (
            <Box
              key={door.doorId}
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID,
                gap: 1.5,
                px: 2.5,
                py: 1.25,
                alignItems: 'center',
                borderTop: i === 0 ? 'none' : `1px solid ${colors.border}`,
                '&:hover': { backgroundColor: colors.bgAlt },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                  backgroundColor: isL ? colors.blue : colors.inkGhost,
                }} />
                <Typography sx={{ fontFamily: font.mono, fontSize: '12px', fontWeight: 500, color: colors.ink }}>
                  {doorLabel(door.doorId)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, maxWidth: 90, height: 5, borderRadius: '2px', backgroundColor: colors.bgAlt, overflow: 'hidden' }}>
                  <Box sx={{
                    width: `${barPct}%`, height: '100%',
                    backgroundColor: isL ? colors.blue : colors.inkGhost,
                  }} />
                </Box>
                <Typography sx={{
                  fontFamily: font.mono, fontSize: '12px',
                  color: door.openings > 0 ? colors.ink : colors.inkGhost,
                }}>
                  {door.openings}
                </Typography>
              </Box>

              <Typography sx={{ fontFamily: font.mono, fontSize: '12px', color: colors.inkMuted }}>
                {fmtMs(door.totalOpenMs)}
                {door.openings > 0 && (
                  <Box component="span" sx={{ color: colors.inkGhost, ml: 1 }}>
                    {t('p2.avg')} {fmtMs(door.avgOpenMs)}
                  </Box>
                )}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: healthColor(door.health), flexShrink: 0 }} />
                <Typography sx={{ fontFamily: font.mono, fontSize: '12px', fontWeight: 500, color: healthColor(door.health) }}>
                  {door.health}%
                </Typography>
              </Box>

              <Box>
                {door.forcedEvents > 0 && <Badge label={`⚠ ${door.forcedEvents}`} t="alert" />}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ── Differential panel ───────────────────────────────────────────────────────

const DifferentialPanel: React.FC<{ stop: StopData }> = ({ stop }) => {
  const { t } = useI18n();
  const lDoors = stop.doors.filter(d => d.doorId.endsWith('L'));
  const total = lDoors.reduce((a, b) => a + b.openings, 0) || 1;
  const busiest = [...lDoors].sort((a, b) => b.openings - a.openings)[0];
  const quietest = [...lDoors].filter(d => d.openings > 0).sort((a, b) => a.openings - b.openings)[0];
  const ratio = quietest && busiest.openings > 0 ? (busiest.openings / quietest.openings).toFixed(1) : '—';

  const wagenStats = (['A', 'B', 'C', 'D'] as const).map(w => ({
    wagen: w,
    openings: stop.doors
      .filter(d => d.doorId.startsWith(w) && d.doorId.endsWith('L'))
      .reduce((a, b) => a + b.openings, 0),
  }));
  const maxWagen = Math.max(...wagenStats.map(w => w.openings), 1);

  const fwdTotal = lDoors.filter(d => d.doorId.includes('1')).reduce((a, b) => a + b.openings, 0);
  const rearTotal = lDoors.filter(d => d.doorId.includes('2')).reduce((a, b) => a + b.openings, 0);
  const fwdPct = Math.round((fwdTotal / (fwdTotal + rearTotal || 1)) * 100);

  return (
    <Box sx={panel}>
      <PanelTitle>{t('p2.differential')}</PanelTitle>

      {/* Forward vs rear */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontFamily: font.mono, fontSize: '11px', fontWeight: 500, color: colors.ink }}>
            {t('p2.forwardPct')} — {fwdPct}%
          </Typography>
          <Typography sx={{ fontFamily: font.mono, fontSize: '11px', color: colors.inkMuted }}>
            {t('p2.rearPct')} — {100 - fwdPct}%
          </Typography>
        </Box>
        <Box sx={{
          height: 10, borderRadius: '2px', overflow: 'hidden', display: 'flex',
          border: `1px solid ${colors.border}`,
        }}>
          <Box sx={{ width: `${fwdPct}%`, backgroundColor: colors.accent }} />
          <Box sx={{ flex: 1, backgroundColor: colors.borderStrong }} />
        </Box>
        <Typography sx={{ fontFamily: font.sans, fontSize: '12px', color: colors.inkFaint, mt: 1 }}>
          {t('p2.unevenNote')}
        </Typography>
      </Box>

      {/* Per wagen */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint, mb: 1.5 }}>
          {t('p2.activityByWagen')}
        </Typography>
        {wagenStats.map(w => (
          <Box key={w.wagen} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Typography sx={{ fontFamily: font.mono, fontSize: '11px', fontWeight: 500, color: colors.inkMuted, width: 22 }}>
              {w.wagen}
            </Typography>
            <Box sx={{
              flex: 1, height: 16, borderRadius: '2px', overflow: 'hidden',
              backgroundColor: colors.bgAlt, border: `1px solid ${colors.border}`,
            }}>
              <Box sx={{
                width: `${(w.openings / maxWagen) * 100}%`,
                height: '100%',
                backgroundColor: WAGEN_COLORS[w.wagen],
                opacity: 0.85,
              }} />
            </Box>
            <Typography sx={{ fontFamily: font.mono, fontSize: '11px', color: colors.inkMuted, width: 62, textAlign: 'right' }}>
              {w.openings}
              <Box component="span" sx={{ color: colors.inkGhost }}>
                {' '}({Math.round((w.openings / total) * 100)}%)
              </Box>
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Key stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1.5 }}>
        {[
          { label: t('p2.busiestDoor'), value: busiest.openings > 0 ? doorLabel(busiest.doorId) : '—', sub: busiest.openings > 0 ? `${busiest.openings} ${t('p2.openings')}` : t('p2.noActivity') },
          { label: t('p2.fwdRear'), value: `${fwdPct}:${100 - fwdPct}`, sub: t('p2.positionSplit') },
          { label: t('p2.doorSpread'), value: `${ratio}×`, sub: t('p2.maxVsMin') },
        ].map(s => (
          <Box key={s.label} sx={{
            backgroundColor: colors.bgAlt,
            border: `1px solid ${colors.border}`,
            borderRadius: layout.radius,
            p: 1.5,
          }}>
            <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint, mb: 0.75 }}>
              {s.label}
            </Typography>
            <Typography sx={{ fontFamily: font.sans, fontSize: '20px', fontWeight: 700, letterSpacing: '-0.02em', color: colors.ink, lineHeight: 1 }}>
              {s.value}
            </Typography>
            <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkFaint, mt: 0.5 }}>
              {s.sub}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ── Health panel ─────────────────────────────────────────────────────────────

const HealthPanel: React.FC<{ stop: StopData }> = ({ stop }) => {
  const { t } = useI18n();
  const flagged = stop.doors.filter(d => d.health < 80 || d.forcedEvents > 0);

  return (
    <Box sx={panel}>
      <PanelTitle right={<Badge label={`${flagged.length} ${t('common.flagged')}`} t={flagged.length ? 'warn' : 'ok'} />}>
        {t('p2.healthPanel')}
      </PanelTitle>

      {flagged.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.ok }} />
          <Typography sx={{ fontFamily: font.sans, fontSize: '13px', color: colors.inkMuted }}>
            {t('p2.allNominal')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {flagged.map(d => {
            const critical = d.health < 65;
            return (
              <Box key={d.doorId} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                borderRadius: layout.radius,
                backgroundColor: critical ? colors.alertSoft : colors.warnSoft,
                borderLeft: `3px solid ${critical ? colors.alert : colors.warn}`,
              }}>
                <Typography sx={{ fontFamily: font.mono, fontSize: '12px', fontWeight: 700, color: colors.ink, width: 44 }}>
                  {doorLabel(d.doorId)}
                </Typography>
                <Typography sx={{ flex: 1, fontFamily: font.sans, fontSize: '12px', color: colors.inkMuted, lineHeight: 1.4 }}>
                  {critical ? t('p2.requiresAttention') : t('common.monitor')} · {t('common.health')} {d.health}%
                  {d.forcedEvents > 0 && ` · ${d.forcedEvents} × ${t('p2.forcedEvents')}`}
                </Typography>
                <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: healthColor(d.health) }}>
                  {healthLabel(d.health, t)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Whole-unit grid */}
      <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${colors.border}` }}>
        <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint, mb: 1.5 }}>
          {t('p2.unitHealth')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {stop.doors.map(d => (
            <Box
              key={d.doorId}
              title={`${doorLabel(d.doorId)}: ${d.health}%`}
              sx={{
                width: 36, height: 30, borderRadius: '2px',
                backgroundColor: colors.bg,
                border: `1px solid ${colors.border}`,
                borderBottom: `3px solid ${healthColor(d.health)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'default',
              }}
            >
              <Typography sx={{ fontFamily: font.mono, fontSize: '9px', fontWeight: 500, color: colors.inkMuted }}>
                {doorLabel(d.doorId)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const TRIP_LABEL_KEYS: Record<string, string> = {
  S1042: 'p2.trip.morningPeak',
  S1148: 'p2.trip.midday',
  S1712: 'p2.trip.eveningPeak',
  S2036: 'p2.trip.lateEvening',
};

const TripSelector: React.FC<{
  tripId: string;
  onChange: (id: string) => void;
}> = ({ tripId, onChange }) => {
  const { t } = useI18n();
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      flexWrap: 'wrap',
      mb: 3,
      pb: 2.5,
      borderBottom: `1px solid ${colors.border}`,
    }}>
      <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint, mr: 1 }}>
        {t('p2.trip')}
      </Typography>
      {TRIPS.map(tr => {
        const active = tr.id === tripId;
        return (
          <Button
            key={tr.id}
            onClick={() => onChange(tr.id)}
            disableRipple
            sx={{
              textTransform: 'none',
              px: 1.75,
              py: 0.9,
              minWidth: 0,
              borderRadius: layout.radius,
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0,
              backgroundColor: active ? colors.blue : colors.surface,
              border: `1px solid ${active ? colors.blue : colors.border}`,
              color: active ? colors.onDark : colors.inkMuted,
              '&:hover': {
                backgroundColor: active ? colors.blueDeep : colors.bgAlt,
                borderColor: active ? colors.blueDeep : colors.borderStrong,
                color: active ? colors.onDark : colors.ink,
              },
            }}
          >
            <Box component="span" sx={{
              fontFamily: font.mono,
              fontSize: '12px',
              fontWeight: 700,
              lineHeight: 1.3,
              color: active ? colors.accent : 'inherit',
            }}>
              {tr.id} · {tr.departure}
            </Box>
            <Box component="span" sx={{
              fontFamily: font.sans,
              fontSize: '11px',
              fontWeight: 400,
              lineHeight: 1.3,
              opacity: 0.75,
            }}>
              {t(TRIP_LABEL_KEYS[tr.id] ?? tr.label)}
            </Box>
          </Button>
        );
      })}
    </Box>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────

const Prototype2: React.FC = () => {
  const { unitId, caption } = useFleet();
  const { t } = useI18n();
  const [tripId, setTripId] = useState(TRIPS[0].id);
  const [selectedStop, setSelectedStop] = useState(0);

  const stops = useMemo(() => getStops(unitId, tripId), [unitId, tripId]);
  const trip = TRIPS.find(tr => tr.id === tripId) ?? TRIPS[0];

  // Guard against an out-of-range index if a future trip has fewer stops.
  const stopIndex = Math.min(selectedStop, stops.length - 1);
  const stop = stops[stopIndex];

  const totalOpenings = stop.doors.reduce((a, b) => a + b.openings, 0);
  const totalForced = stop.doors.reduce((a, b) => a + b.forcedEvents, 0);
  const activeDoors = stop.doors.filter(d => d.openings > 0).length;

  const handleSelect = useCallback((i: number) => setSelectedStop(i), []);

  const handleTripChange = useCallback((id: string) => {
    setTripId(id);
    setSelectedStop(0);
  }, []);

  return (
    <PrototypeLayout protoId="02" titleKey="proto.02.title">
      <PageHeader
        eyebrow={caption}
        title={t('p2.title')}
        right={
          <>
            <Badge label={t('common.syntheticData')} t="info" />
            <Typography sx={{ ...ty.meta, color: colors.inkFaint }}>
              {ROUTE_CAPTION}
            </Typography>
          </>
        }
      />

      <TripSelector tripId={tripId} onChange={handleTripChange} />

      <OverviewChart stops={stops} selectedStop={stopIndex} onSelectStop={handleSelect} />

      <Typography sx={{ ...ty.eyebrow, color: colors.inkFaint, mb: 1.5 }}>
        {t('p2.selectStop')}
      </Typography>
      <StopSelector stops={stops} selected={stopIndex} onChange={handleSelect} />

      {/* Selected stop banner */}
      <Box sx={{
        backgroundColor: colors.accentWash,
        border: `1px solid ${colors.border}`,
        borderLeft: `4px solid ${colors.accent}`,
        borderRadius: layout.radiusLg,
        p: 2.5,
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 3,
      }}>
        <Box>
          <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint, mb: 0.5 }}>
            {t('p2.stopOf', { i: stopIndex + 1, n: stops.length })} · {trip.id} · {t('p2.arr')} {stop.arrivalTime}
            {stop.role !== 'intermediate' && ` · ${stop.role}`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
            <Typography sx={{
              fontFamily: font.mono, fontSize: '20px', fontWeight: 700,
              color: colors.accentDeep, lineHeight: 1.1,
            }}>
              {stop.code}
            </Typography>
            <Typography sx={{
              fontFamily: font.sans, fontSize: '26px', fontWeight: 700,
              letterSpacing: '-0.02em', color: colors.ink, lineHeight: 1.1,
            }}>
              {stop.stopName}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {[
            { label: t('p2.totalOpenings'), value: String(totalOpenings) },
            { label: t('p2.activeDoors'), value: `${activeDoors}/16` },
            { label: t('p2.dwellTime'), value: fmtMs(stop.dwellMs) },
            { label: t('p2.forcedEvents'), value: String(totalForced), danger: totalForced > 0 },
          ].map(s => (
            <Box key={s.label}>
              <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint, mb: 0.5 }}>
                {s.label}
              </Typography>
              <Typography sx={{
                fontFamily: font.sans, fontSize: '22px', fontWeight: 700,
                letterSpacing: '-0.02em', lineHeight: 1,
                color: s.danger ? colors.alert : colors.ink,
              }}>
                {s.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Heatmap */}
      <Box sx={{ ...panel, mb: 3 }}>
        <PanelTitle>{t('p2.heatmapTitle')}</PanelTitle>
        <StopHeatmap stop={stop} />
      </Box>

      {/* Table */}
      <Typography sx={{ ...ty.eyebrow, color: colors.inkFaint, mb: 1.5 }}>
        {t('p2.perDoor')}
      </Typography>
      <Box sx={{ mb: 3 }}>
        <DoorTable stop={stop} />
      </Box>

      {/* Panels */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: 3,
      }}>
        <DifferentialPanel stop={stop} />
        <HealthPanel stop={stop} />
      </Box>

      <DemoFooter note={t('common.syntheticNote')} />
    </PrototypeLayout>
  );
};

export default Prototype2;
